<template>
	<v-container fluid>
		<div class="d-flex flex-wrap py-2">
			<v-btn class="ma-1" color="primary" small @click="getFacturas">
				<v-icon left>mdi-reload</v-icon>Recargar
			</v-btn>
			<v-btn
				class="ma-1"
				color="primary"
				small
				to="/autofacturar"
				v-if="$root.acceso('CREAR_FACTURA_COLABORADORES')"
			>
				<v-icon left>mdi-plus</v-icon>Contratos sin facturar
			</v-btn>
			<v-btn class="ma-1" color="primary" small @click="exportarAcumulativoAnual" :disabled=" !selected.length == 0 && !selected.every( f => f.estadoFactura == 'Pagada' ) ">
					<v-icon left>mdi-database-export-outline</v-icon>Exportar acumulativo anual
			</v-btn>
		</div>
		<v-card>
			<v-data-table
				fixed-header
				:items="facturas"
				:headers="facturasHeader"
				show-select
				item-key="idFactura"
				v-model="selected"
				:single-select="false"
				:calculate-widths="true"
				:search="search"
				:loading="loading"
				:footer-props="{
					showFirstLastPage: true,
					showCurrentPage: true,
					itemsPerPageOptions: [10, 25, 50],
				}"
				checkbox-color="secondary"
			>
				<template v-slot:body.prepend="{ headers }">
					<table-filters :headers="headers" :items="facturas" v-model="inlineFilters"></table-filters>
				</template>

				<template v-slot:item.fechaFactura="{ item }">
					<span v-text="parseDate(item.fechaFactura)"></span>
				</template>

				<template v-slot:item.baseImponible="{ item }">
					<span v-text="parseFloat(item.baseImponible).toFixed(2) + ' €'" />
				</template>
				<template v-slot:item.iva="{ item }">
					<span v-text="`${parseFloat(item.iva).toFixed(2)} €`" />
				</template>
				<template v-slot:item.irpf="{ item }">
					<span v-text="`${parseFloat(item.irpf).toFixed(2)} €`" />
				</template>
				<template v-slot:item.porcentajeIva="{ item }">
					<v-chip label v-text="item.porcentajeIva * 1 + '%'" />
				</template>
				<template v-slot:item.porcentajeIrpf="{ item }">
					<v-chip label v-text="item.porcentajeIrpf * 1 + '%'" />
				</template>
				<template v-slot:item.totalFactura="{ item }">
					<span class="font-weight-bold" v-text="parseFloat(item.totalFactura).toFixed(2) + ' €'" />
				</template>

				<template v-slot:item.estadoFactura="{ item }">
					<status-chip :value="item.estadoFactura" />
				</template>

				<template v-slot:item.numeroFactura="{ item }">
					<v-tooltip bottom v-if="item.archivo">
						<template v-slot:activator="{ on, attrs }">
							<v-btn
								v-on="on"
								v-bind="attrs"
								link
								color="secondary"
								text
								:loading="pdfId === item.idFactura"
								:disabled="pdfId"
								@click="verPDF(item.archivo)"
							>
								{{ item.numeroFactura }}
								<v-icon right>mdi-file-document-edit-outline</v-icon>
							</v-btn>
						</template>
						<span>Ver PDF factura</span>
					</v-tooltip>
					<span
						v-else-if="item.numeroFactura"
						link
						color="secondary"
						text
						class="text-button secondary--text"
					>{{ item.numeroFactura }}</span>
				</template>

				<template v-slot:item.CodigoContrato="{ item }">
					<v-tooltip bottom>
						<template v-slot:activator="{ on, attrs }">
							<v-btn
								v-on="on"
								v-bind="attrs"
								link
								:to="`/detalles_contrato?codigoContrato=${item.CodigoContrato}&idContrato=${item.IdContrato}`"
								color="secondary"
								text
							>{{ item.CodigoContrato }}</v-btn>
						</template>
						<span>Detalles del contrato</span>
					</v-tooltip>
				</template>

				<template v-slot:item.acciones="{ item }">
					<v-tooltip bottom>
						<template v-slot:activator="{ on, attrs }">
							<v-btn v-on="on" v-bind="attrs" @click="detailsId = item.idFactura" icon color="secondary">
								<v-icon>mdi-eye</v-icon>
							</v-btn>
						</template>
						Ver detalles
					</v-tooltip>
				</template>
			</v-data-table>
		</v-card>

		<v-dialog
			:value="detailsId"
			@input="(v) => v === false ? detailsId = null : null"
			fullscreen
			hide-overlay
			transition="dialog-bottom-transition"
		>
			<v-card class="rounded-0">
				<v-toolbar color="primary" class="white--text">
					<v-toolbar-title>Detalles Factura</v-toolbar-title>
					<v-spacer />
					<v-btn color="white" icon @click="detailsId = null">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-toolbar>
				<v-card-text>
					<detalles-facturacion
						@reload="getFacturas"
						@close="detailsId = null; getFacturas()"
						:idFactura="detailsId"
						v-if="detailsId"
						:key="detailsId"
					/>
				</v-card-text>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
import { getSituacionIcon, perColumnFilter, parseDate, downloadFile } from "@/utils/index.js";

export default {
	components: {
		TableFilters: () => import("@/components/TableFilters.vue"),
		DetallesFacturacion: () => import("@/views/facturacionColaboradores/DetallesFactura.vue"),
		StatusChip: () => import("@/components/StatusChip.vue"),
	},
	props: ['codigoContrato'],
	data() {
		return {
			debounce: true,
			loading: true,
			page: 2,
			selected: [],
			search: null,
			detailsId: this.$route.query.idFactura || null,
			pdfId: false,
			facturas: [],
			facturasHeader: [
				{ text: "Estado", dataType: "select", value: "estadoFactura" },
				{ text: "Acciones", value: "acciones", sortable: false, filterable: false, },
				{ text: "NºDoc", value: "numeroFactura" },
				{ text: "Fecha", dataType: "date", value: "fechaFactura" },
				{ text: "Canal", dataType: "select", value: "canal" },
				{ text: "Importe base", value: "baseImponible" },
				{ text: "% IVA", value: "porcentajeIva" },
				{ text: "€ IVA", value: "iva" },
				{ text: "% IRPF", value: "porcentajeIrpf" },
				{ text: "€ IRPF", value: "irpf" },
				{ text: "Total", value: "totalFactura" },
				{ text: "Numero líneas", value: "nLineas" },
				{ text: 'Tramitado por', value: 'Tramitador' }
			].map((header) => {
				return {
					class: "text-no-wrap sticky-header",
					cellClass: "pa-2 text-no-wrap",
					filter: (value) =>
						perColumnFilter(
							value,
							this.inlineFilters[header.value],
							header.dataType
						),
					dataType: "text",
					...header,
				};
			}),

			inlineFilters: {},
			comboboxes: {},
			loadingPDF: null,
		};
	},
	methods: {
		getSituacionIcon,
		parseDate,
		async getFacturas() {
			this.loading = true;
			const { data: facturas } = await axios({
				url: `${process.env.VUE_APP_OUR_API_URL}/autoFacturas.php`,
				method: 'GET',
				params: { token: this.$store.getters.getJwtEmpresa }
			});
			this.facturas = facturas;

			this.loading = false;
		},
		verPDF(archivo) {
			axios({
				method: 'GET',
				url: `${process.env.VUE_APP_OUR_API_URL}/autofacturar.php`,
				params: {
					token: this.$store.getters.getJwtEmpresa,
					archivo: archivo,
				},
				responseType: 'blob',
			}).then(res => {
				downloadFile(new File([res.data], archivo, { type: 'application/pdf' }), true);
			}).catch(err => {
				console.error(err);
				this.$root.$emit('snack', 'Ha ocurrido un error');
			})
		},
		exportarAcumulativoAnual() {
			let canal = [];
			if (this.selected.length > 0) {
				canal = [...new Set(this.selected.map( s => s.canal ))]
			} else {
				canal = [...new Set(this.facturas.map( s => s.canal ))]
			}
			axios({
				method: 'GET',
				url: `${process.env.VUE_APP_OUR_API_URL}/acumulativoAnual.php`,
				responseType: 'blob',
				params: {
					canal,
					token: this.$store.getters.getJwtEmpresa,
				}
			}).then(res => {
				const MESES = [
					"Enero",
					"Febrero",
					"Marzo",
					"Abril",
					"Mayo",
					"Junio",
					"Julio",
					"Agosto",
					"Septiembre",
					"Octubre",
					"Noviembre",
					"Diciembre",
				];
				let excel = new File(
					[res.data],
					`Acumulativo_comisiones_completo_${MESES[new Date().getMonth()]}_${new Date().getFullYear()}.xlsx`,
					{
						type: "application/excel",
					}
				);
				downloadFile(excel, false);
			})
		}
	},
	mounted() {
		this.getFacturas();
	},
};
</script>

<style scoped>
.v-data-table /deep/ [role="columnheader"] {
	/* background: #f1f1f1 !important; */
}
</style>
